import type { ComponentProps } from 'react';
import React, { forwardRef } from 'react';
import { Link } from 'expo-router';
import { StyleSheet, View, type ViewInstance } from 'react-native';

import { A } from '@expo/html-elements';

import {
  checkIfInternalUrl,
  formatUniversalURL,
  getAppAssociatedDomains,
} from '../../utils/linking';

import type { PressableLinkProps } from './PressableLink.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders an anchor element (`a`) that handles universal
 * (internal and external) URLs.
 *
 * @see {@link https://sweetgreen.atlassian.net/wiki/spaces/TD/pages/4764663963/How+to+Handling+universal+links}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a}
 */
export const PressableLink = forwardRef<ViewInstance, PressableLinkProps>((props, ref) => {
  const { to, children, style, onPress, ...restProps } = props;

  const formattedURL = formatUniversalURL(to, APP_ASSOCIATED_DOMAINS);
  const isInternalURL = checkIfInternalUrl(formattedURL);

  // ─────────────────────────────────────────────────────────────────────

  // For internal URLs, render expo-router's `Link` to handle in-app
  // navigation without reloading the app or creating a new tab.

  if (isInternalURL) {
    return (
      <View ref={ref}>
        <Link
          href={formattedURL}
          style={style}
          accessibilityRole="link"
          onPress={onPress}
          {...restProps}
        >
          {children}
        </Link>
      </View>
    );
  }

  // For external URL, we use an anchor element (`a`) to open it in a new
  // tab with assigning the relevant `rel` attributes to improve sandboxing.

  return (
    <A
      ref={ref as ComponentProps<typeof A>['ref']}
      href={formattedURL}
      // @ts-expect-error TS(2322): Type '((false | "" | ViewStyle | RegisteredStyle<V... Remove this comment to see the full error message
      style={[styles.anchorDefaultStyles, style]}
      onPress={onPress}
      target={EXTERNAL_LINK_ATTRS.hrefAttrs.target}
      rel={EXTERNAL_LINK_ATTRS.hrefAttrs.rel}
      {...EXTERNAL_LINK_ATTRS}
      {...restProps}
    >
      {children}
    </A>
  );
});

// ─── Constants ───────────────────────────────────────────────────────────────

const APP_ASSOCIATED_DOMAINS = getAppAssociatedDomains();

const EXTERNAL_LINK_ATTRS = {
  hrefAttrs: { target: '_blank', rel: 'noopener noreferrer' },
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  // To match the styles of other components, we must add `default` flex styles
  // to the web-only `A` component.
  anchorDefaultStyles: {
    display: 'flex',
    flexDirection: 'column',
  },
});
